import { useAuth0 } from "@auth0/auth0-react";
import { useForm } from "@mantine/form";
import { useEffect, useRef, useState } from "react";
import CompanyInfoForm from "components/forms/CompanyInfoForm";
import { postMultipart } from "utils/ApiClient";
import { Button, Modal, Text } from "@mantine/core";
import ModalButtons from "components/ui/Modal/ModalButtons";
import { notifications } from '@mantine/notifications';
import { MY_SITE_URL } from "constants/navs";

interface FormValues {
    companyBrandName: string;
    yourName: string;
    yourEmail: string;
    logoDisplayDate: Date | null;
    websiteUrl: string;
    companyDescription: string;
    rasterImages: File[];
    additionalComments: string;
    submitterId: string;
}

const CompanyInfoSubmission = () => {
    const { user } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const form = useForm<FormValues>({
        initialValues: {
            companyBrandName: "",
            yourName: "",
            yourEmail: "",
            logoDisplayDate: null,
            websiteUrl: "",
            companyDescription: "",
            rasterImages: [],
            additionalComments: "",
            submitterId: user?.sub || "",
        },
        validate: {
            companyBrandName: (value) =>
                value.trim().length > 0 ? null : "Company name is required",
            yourName: (value) =>
                value.trim().length > 0 ? null : "Your name is required",
            yourEmail: (value) =>
                /^\S+@\S+\.\S+$/.test(value) ? null : "Invalid email address",
            logoDisplayDate: (value) =>
                value ? null : "Logo display date is required",
            websiteUrl: (value) =>
                value.trim().length > 0 ? null : "Website URL is required",
            companyDescription: (value) => {
                const wordCount = value.trim().split(/\s+/).length;
                return wordCount <= 75 ? null : "Description must not exceed 75 words";
            },
            rasterImages: (value) =>
                value.length === 1 ? null : "Logo image is required",
        },
    });

    const fieldRefs = {
        companyBrandName: useRef<HTMLInputElement>(null),
        yourName: useRef<HTMLInputElement>(null),
        yourEmail: useRef<HTMLInputElement>(null),
        logoDisplayDate: useRef<HTMLButtonElement>(null),
        websiteUrl: useRef<HTMLInputElement>(null),
    };

    // Auto-dismiss error after 3 seconds
    // Update submitterId when user loads from Auth0
    useEffect(() => {
        if (user?.sub) {
            form.setFieldValue('submitterId', user.sub);
        }
    }, [user?.sub, form]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 3000);

            // Cleanup timer if component unmounts or error changes
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        const errorKeys = Object.keys(form.errors);

        if (errorKeys.length === 0) return;

        const fieldOrder: (keyof typeof fieldRefs)[] = [
            "companyBrandName",
            "yourName",
            "yourEmail",
            "logoDisplayDate",
            "websiteUrl",
        ];

        const firstErroredKey = fieldOrder.find((key) => errorKeys.includes(key));

        if (firstErroredKey) {
            const ref = fieldRefs[firstErroredKey];
            if (ref?.current) {
                ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [form.errors]);

    const handleSubmit = async (values: FormValues) => {
        try {
            setLoading(true);
            setError(null);

            // Create FormData for file uploads
            const formData = new FormData();

            // Add form fields
            formData.append('companyBrandName', values.companyBrandName);
            formData.append('yourName', values.yourName);
            formData.append('yourEmail', values.yourEmail);
            formData.append('logoDisplayDate', values.logoDisplayDate ? values.logoDisplayDate.toISOString().split('T')[0] : '');
            formData.append('websiteUrl', values.websiteUrl);
            formData.append('companyDescription', values.companyDescription);
            formData.append('additionalComments', values.additionalComments);
            formData.append('submitterId', values.submitterId);

            // Add raster images
            values.rasterImages.forEach((file) => {
                formData.append('rasterImages', file);
            });

            // Submit to backend API using the existing API client
            const response = await postMultipart('/company/submit', formData);

            if (response.data.success) {
                setModalOpen(true);
                form.reset();
            } else {
                notifications.show({
                    title: 'Submission Failed',
                    message: 'Failed to submit company information. Please try again.',
                    color: 'red',
                });
                throw new Error(response.data.message || "Failed to submit company information");
            }

        } catch (error: any) {
            console.error("Submission error:", error);
            const errorMessage = error.response?.data?.error || error.message || "An error occurred during submission";
            setError(errorMessage);
            notifications.show({
                title: 'Submission Failed',
                message: 'Failed to submit company information. Please try again.',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <CompanyInfoForm
                form={form}
                fieldRefs={fieldRefs}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
            />
            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Company Logo Submitted Successfully"
                centered
            >
                <Text>Would you like to submit another logo?</Text>

                <ModalButtons>
                    <Button
                        onClick={() => {
                            form.reset();
                            setModalOpen(false);
                        }}
                    >
                        Yes
                    </Button>

                    <Button variant="default" onClick={() => window.location.replace(MY_SITE_URL)}>
                        No
                    </Button>
                </ModalButtons>
            </Modal>
        </>
    );
};

export default CompanyInfoSubmission;